import { action, observable } from 'mobx';

// Dependencies
import { useStaticRendering } from 'mobx-react';
import dateFormat from 'dateformat';
import ApiCore from '../ApiCore';

// Helpers
import { nextBillingDate } from '../../helpers/cart';

const isServer = !process.browser;
useStaticRendering(isServer);

class CheckoutStore {
  @observable countries = [];

  @observable order = {};

  @observable paymentLoad = false;

  /**
   * Creates an order and a customer account if createAccount is defined
   * and/or subsription is the order type
   * @param {object} order
   */
  @action submit = async (order, password, user, loggedIn) => {
    const postOrder = { ...order };
    const { createAccount } = postOrder;
    let customer = {};
    const returnData = { orderId: 0, subscriptionOrderId: 0 };

    if (postOrder.line_items.length < 1) return 'empty_cart';

    if (createAccount && !loggedIn) {
      customer = {
        email: postOrder.billing.email,
        first_name: postOrder.billing.first_name,
        last_name: postOrder.billing.last_name,
        password,
        shipping: postOrder.shipping,
        billing: postOrder.billing,
      };

      // Create customer
      await ApiCore.post('/wc/v3/customers', customer)
        .then((response) => {
          customer = response.data;

          // Set customer ID if customer id is defined
          if (customer.id) postOrder.customer_id = customer.id;

          return response;
        })
        .catch(error => Promise.reject(error.response));
    }

    await ApiCore.post('/wc/v3/orders', postOrder)
      .then((response) => {
        postOrder.parent_id = response.data.id;
        returnData.orderId = response.data.id;

        return response;
      }).catch(error => Promise.reject(error.response));

    if (postOrder.line_items[0].type === 'subscription') {
      postOrder.billing_period = postOrder.line_items[0].subscription_period.value;
      postOrder.billing_interval = postOrder.line_items[0].subscription_length.value;
      postOrder.start_date = dateFormat(new Date(), 'yyyy-m-d H:M:s');
      postOrder.next_payment_date = dateFormat(
        nextBillingDate(
          postOrder.line_items[0].subscription_length.value,
          postOrder.line_items[0].subscription_period.value,
        ),
        'yyyy-m-d H:M:s',
      );

      await ApiCore.post('/wc/v1/subscriptions', postOrder)
        .then((response) => {
          returnData.subscriptionOrderId = response.data.id;

          return response;
        })
        .catch(error => Promise.reject(error.response));
    }

    return returnData;
  }

  @action async show(orderId) {
    ApiCore.get(`/wc/v3/orders/${orderId}`).then((response) => {
      this.order = response.data;

      return response;
    });
  }

  @action update = async (orderId, subId, data) => {
    await ApiCore.post(`/wc/v3/orders/${orderId}`, {
      status: 'completed',
      set_paid: true,
    })
      .then(response => response)
      .catch(error => Promise.reject(error.response));

    if (subId !== 0 && subId !== '0') {
      await ApiCore.post(`/wc/v1/subscriptions/${subId}`, {
        status: 'active',
        payment_method: 'stripe',
        payment_details: {
          post_meta: {
            _stripe_customer_id: data.customer.id,
            _stripe_card_id: data.customer.default_source,
          },
        },
      })
        .then(response => response)
        .catch(error => Promise.reject(error.response));
    }
  }

  @action async getCountries() {
    ApiCore.get('/wc/v3/data/countries')
      .then((response) => {
        this.countries = response.data;

        return response;
      });
  }

  /**
   * Set payment loader screen
   * @param {boolean} load
   */
  @action async paymentLoader(load) {
    this.paymentLoad = load;
  }
}

let checkoutStore = null;

export default function initializeCheckoutStore(initialData) {
  if (isServer) {
    return new CheckoutStore(isServer, initialData);
  }

  if (checkoutStore === null) {
    checkoutStore = new CheckoutStore(isServer, initialData);
  }

  return checkoutStore;
}
